import { Box, Checkbox, IconButton, Typography, useTheme } from '@mui/material';
import { Check, Info, MoreVertical } from 'lucide-react';
import { memo } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import type { SeriesDto } from '~/backend/connector-next-api/connector-next-schemas';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import StatusLine from '~/components/Status/StatusLine';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import type { WorkflowStepId } from '~/containers/viewer/explorer-sidebar/WorkflowStepper/workflow-steps';
import type { OpenIntent } from '~/hooks/openInViewer/resolveOpenIntent';
import { formatMmAsCmAtScale } from '~/utils/units';
import type { DatasetInfoControls } from '../hooks';
import { PrimaryStar, QuietButton, SYNC_TONE_COLORS } from '../shared';
import ModalityChip from '~/components/Chip/ModalityChip';
import DatasetOpenButton from './DatasetOpenButton';
import {
    datasetSyncStatusMeta,
    primaryDatasetKind,
    roiNamesPreview,
    seriesDisplayName,
    seriesKind,
    seriesModality,
    sliceGeometry,
} from '../model';
import DatasetInfoTooltip from './DatasetInfoTooltip';

/** Width of the leading control column, so every row mode keeps the same gutter. */
const CONTROL_WIDTH = 18;

/** The ⓘ control and its details card, offered where the row's patient context is known. */
export type DatasetInfoOffer = {
    controls: DatasetInfoControls;
    /** Every series of the same patient, to name what this one references. */
    siblings: readonly SeriesDto[];
};

/** What an openable row needs to open itself. */
type DatasetOpen = {
    onOpenObject: (intent: OpenIntent) => void;
    /** The workflow the course stage leads to; its action becomes the button. */
    stageWorkflow: WorkflowStepId | null;
    /** True while another open is in flight; the row's button is refused. */
    openBusy: boolean;
    /**
     * The patient's designations, so a row can say whether it holds one.
     * Absent where the panel cannot write them — with no patient version
     * there is nothing to guard the write with, and a star that cannot be
     * clicked is worse than no star.
     */
    primary?: DatasetPrimary;
};

/** What the panel knows about the patient's designations, and how to change one. */
export type DatasetPrimary = {
    imageId: string | null;
    structureSetId: string | null;
    /**
     * `isPrimary` says what the clicked row currently is, so the caller can
     * tell "designate this" from "clear the designation" without re-deriving
     * it from ids it has already compared.
     */
    onToggle: (change: {
        kind: 'image' | 'structureSet';
        datasetId: string;
        isPrimary: boolean;
    }) => void;
    busy: boolean;
};

/**
 * A row that opens (the patient panel), one that selects (the import review,
 * where choosing what to import is the whole task), or one that just states
 * its own status.
 */
type DatasetRowProps = (
    | ({
          series: SeriesDto;
          control: 'open';
          /**
           * Message id saying why this row cannot be opened. The affordance
           * stays and carries the reason: a control that is simply absent
           * reads as a build that forgot it, and SR-WOR-H-37-3 asks for the
           * refusal before the user commits rather than after.
           */
          openRefusalId?: string;
      } & DatasetOpen)
    | {
          series: SeriesDto;
          control: 'select';
          seriesId: string;
          checked: boolean;
          onToggle: (seriesId: string) => void;
      }
    | {
          series: SeriesDto;
          control: 'imported' | 'alreadyStored' | 'none';
      }
) & {
    /** Key the details card is tracked under; undefined offers no ⓘ. */
    infoKey?: string;
    info?: DatasetInfoOffer;
    /** Whether this row's details are showing, and whether a click pinned them. */
    infoOpen?: boolean;
    infoPinned?: boolean;
};

/** One dataset (series) row of the patient panel. */
const DatasetRow = memo(function DatasetRow(props: DatasetRowProps) {
    const {
        series,
        infoKey,
        info,
        infoOpen = false,
        infoPinned = false,
    } = props;
    const intl = useIntl();
    const theme = useTheme();
    const kind = seriesKind(series);
    const sliceCount = series.slices?.length ?? 0;
    const structureCount = series.structureSet?.structureCount;
    const subtitle =
        kind === 'image'
            ? sliceGeometryText(intl, series)
            : roiNamesPreview(series);
    const syncStatus = datasetSyncStatusMeta(intl, series.status);
    const name = seriesDisplayName(intl, series);
    const selection = props.control === 'select' ? props : undefined;
    const onToggleRow = selection
        ? () => selection.onToggle(selection.seriesId)
        : undefined;
    return (
        <Box
            onClick={onToggleRow}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                minHeight: 58,
                px: 3,
                borderBottom: 1,
                borderColor: 'divider',
                cursor: onToggleRow ? 'pointer' : 'default',
                transition: 'background 100ms',
                bgcolor: rowBackground(infoOpen, selection?.checked === true),
                '&:hover, &:focus-within': onToggleRow
                    ? {
                          bgcolor: infoOpen
                              ? 'action.selected'
                              : 'action.hoverNeutral',
                      }
                    : undefined,
            }}
        >
            {props.control === 'select' ? (
                /* Own click is swallowed so the row handler cannot toggle a second time. */
                <Box
                    component="span"
                    onClick={(event) => event.stopPropagation()}
                    sx={{ display: 'inline-flex' }}
                >
                    <Checkbox
                        checked={props.checked}
                        onChange={onToggleRow}
                        disableRipple
                        inputProps={{
                            'aria-label': intl.formatMessage(
                                { id: 'patientList.dataset.select' },
                                { name }
                            ),
                        }}
                        sx={{ p: 0 }}
                    />
                </Box>
            ) : (
                <PrimaryMark {...props} description={name} />
            )}
            <ModalityChip modality={seriesModality(series)} />
            <Typography
                component="span"
                noWrap
                sx={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: 'text.primary',
                    // The row ends in a button. Without a shrinkable name a
                    // long series description pushes that button past the
                    // panel's edge and out of reach.
                    flexShrink: 1,
                    minWidth: 0,
                }}
            >
                <SensitiveInfoTypography>{name}</SensitiveInfoTypography>
            </Typography>
            <Typography
                component="span"
                noWrap
                sx={{
                    fontSize: 13,
                    color: 'text.secondary',
                    flex: 1,
                    minWidth: 0,
                }}
            >
                {subtitle ?? ''}
            </Typography>
            {props.control === 'alreadyStored' && (
                <Typography
                    component="span"
                    noWrap
                    sx={{
                        fontSize: 13,
                        color: 'success.main',
                        flexShrink: 0,
                    }}
                >
                    {intl.formatMessage({
                        id: 'patientList.dataset.alreadyStored',
                    })}
                </Typography>
            )}
            {info !== undefined && infoKey !== undefined && (
                <Box
                    component="span"
                    onClick={(event) => event.stopPropagation()}
                    sx={{ display: 'inline-flex', flexShrink: 0 }}
                >
                    <DatasetInfoTooltip
                        open={infoOpen}
                        onClose={() => info.controls.onHover(undefined)}
                        series={series}
                        siblings={info.siblings}
                    >
                        <IconButton
                            size="small"
                            selected={infoOpen}
                            aria-label={intl.formatMessage(
                                { id: 'patientList.dataset.info.open' },
                                { name }
                            )}
                            aria-pressed={infoPinned}
                            onMouseEnter={() => info.controls.onHover(infoKey)}
                            onMouseLeave={() =>
                                info.controls.onHover(undefined)
                            }
                            onFocus={() => info.controls.onHover(infoKey)}
                            onBlur={() => info.controls.onHover(undefined)}
                            onClick={() => info.controls.onToggle(infoKey)}
                            sx={{
                                color: infoOpen
                                    ? 'primary.main'
                                    : 'text.secondary',
                            }}
                        >
                            <Info size={16} />
                        </IconButton>
                    </DatasetInfoTooltip>
                </Box>
            )}
            <Typography
                variant="monoMeta"
                sx={{ fontSize: 13, minWidth: 64, textAlign: 'right' }}
            >
                {kind === 'image' && sliceCount > 0
                    ? intl.formatMessage(
                          { id: 'patientList.dataset.sliceCount' },
                          { count: sliceCount }
                      )
                    : ''}
                {kind === 'structureSet' && structureCount !== undefined
                    ? intl.formatMessage(
                          { id: 'patientList.dataset.roiCount' },
                          { count: structureCount }
                      )
                    : ''}
            </Typography>
            {syncStatus && (
                <Box component="span" sx={{ flexShrink: 0, minWidth: 110 }}>
                    <StatusLine
                        label={syncStatus.label}
                        color={SYNC_TONE_COLORS[syncStatus.tone](theme)}
                        size={14}
                    />
                </Box>
            )}
            <Box
                component="span"
                onClick={(event) => event.stopPropagation()}
                sx={{ display: 'inline-flex', flexShrink: 0 }}
            >
                {props.control === 'open' &&
                series.id &&
                !props.openRefusalId ? (
                    <DatasetOpenButton
                        series={series}
                        datasetId={series.id}
                        stageWorkflow={props.stageWorkflow}
                        disabled={props.openBusy}
                        onOpen={props.onOpenObject}
                    />
                ) : props.control === 'open' ? (
                    // Not openable — no connector id to address it by, or a
                    // dataset nothing can display. The affordance stays so
                    // rows keep one gutter, and says why it cannot be used.
                    <DisabledHint
                        title={intl.formatMessage({
                            id:
                                props.openRefusalId ||
                                'patientList.dataset.actionsHint',
                        })}
                    >
                        <QuietButton
                            disabled
                            aria-label={intl.formatMessage({
                                id: 'patientList.dataset.actions',
                            })}
                        >
                            <MoreVertical size={12} />
                        </QuietButton>
                    </DisabledHint>
                ) : null}
            </Box>
        </Box>
    );
});

/**
 * Leading gutter of a non-selectable row. An openable row of a kind that can
 * be designated carries its primary star there; every other row carries the
 * status mark or an empty spacer, so all rows keep one gutter.
 */
function PrimaryMark(
    props: Exclude<DatasetRowProps, { control: 'select' }> & {
        description: string;
    }
) {
    const kind =
        props.control === 'open' ? primaryDatasetKind(props.series) : null;
    const primary = props.control === 'open' ? props.primary : undefined;
    if (kind === null || primary === undefined || !props.series.id) {
        return <RowMark control={props.control} />;
    }
    const held = kind === 'image' ? primary.imageId : primary.structureSetId;
    const datasetId = props.series.id;
    const isPrimary = held === datasetId;
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                justifyContent: 'center',
                flexShrink: 0,
                width: CONTROL_WIDTH,
            }}
        >
            <PrimaryStar
                kind={kind}
                state={isPrimary ? 'primary' : held ? 'other' : 'unset'}
                name={props.description}
                disabled={primary.busy}
                onToggle={() =>
                    primary.onToggle({ kind, datasetId, isPrimary })
                }
            />
        </Box>
    );
}

/**
 * "0.200 cm spacing · 48.60 cm range" for an image series with positioned
 * slices; uneven spacing is named rather than averaged. Undefined below two
 * positioned slices.
 */
function sliceGeometryText(
    intl: IntlShape,
    series: SeriesDto
): string | undefined {
    const geometry = sliceGeometry(series);
    if (!geometry) {
        return undefined;
    }
    const spacing =
        geometry.spacingMm === undefined
            ? intl.formatMessage({ id: 'patientList.dataset.unevenSpacing' })
            : intl.formatMessage(
                  { id: 'patientList.dataset.spacing' },
                  { value: formatMmAsCmAtScale(geometry.spacingMm, 'voxel') }
              );
    const extent = intl.formatMessage(
        { id: 'patientList.dataset.extent' },
        { value: formatMmAsCmAtScale(geometry.extentMm, 'macro') }
    );
    return `${spacing} · ${extent}`;
}

/** Row paint: the details-open tint outranks the checked tint. */
function rowBackground(infoOpen: boolean, checked: boolean): string {
    if (infoOpen) {
        return 'action.selected';
    }
    return checked ? 'action.hover' : 'transparent';
}

/** Leading gutter of a row with no designation to show: a status mark or a spacer. */
function RowMark({
    control,
}: {
    control: 'open' | 'imported' | 'alreadyStored' | 'none';
}) {
    const intl = useIntl();
    // An openable row has no leading mark; the spacer keeps every row's
    // content on the same gutter whether or not it carries one.
    if (control === 'none' || control === 'open') {
        return (
            <Box
                component="span"
                sx={{ width: CONTROL_WIDTH, flexShrink: 0 }}
            />
        );
    }
    return (
        <Box
            component="span"
            role={control === 'imported' ? 'img' : undefined}
            aria-label={
                control === 'imported'
                    ? intl.formatMessage({ id: 'patientList.dataset.imported' })
                    : undefined
            }
            aria-hidden={control === 'imported' ? undefined : true}
            sx={{
                display: 'inline-flex',
                justifyContent: 'center',
                flexShrink: 0,
                width: CONTROL_WIDTH,
                color: 'success.main',
            }}
        >
            <Check size={16} aria-hidden />
        </Box>
    );
}

export default DatasetRow;
